import { createContext, useContext, useState, useEffect, ReactNode } from "react";

type Language = "de" | "en";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType>({
  language: "de",
  setLanguage: () => {},
  t: (key: string) => key,
});

const translations: Record<Language, Record<string, string>> = {
  de: {
    "nav.features": "Features",
    "nav.appPreview": "App Preview",
    "nav.waitlist": "Warteliste",
    "nav.menuOpen": "Menü öffnen",

    "hero.main": "Finanzbildung, die du ",
    "hero.highlight": "wirklich brauchst",
    "hero.sub": "In deiner Sprache, in deinem Alltag. Lerne Finanzen spielerisch und praxisnah.",
    "hero.cta": "Frühen Zugang sichern",
    "hero.free": "Kostenlos",
    "hero.ai": "AI-Coach",
    "hero.swiss": "CH-Finanzwissen",

    "features.title": "Warum finbeam?",
    "features.sub": "Alles, was dir in der Schule niemand erklärt hat – in kleinen Häppchen.",
    "features.1.title": "Personalisierte Lernziele",
    "features.1.desc": "Setze dir realistische Ziele und verfolge deinen Fortschritt in Echtzeit",
    "features.2.title": "Interaktive Module",
    "features.2.desc": "Lerne durch Quiz, Challenges und praktische Übungen statt langweiliger Theorie",
    "features.3.title": "Praktische Tools",
    "features.3.desc": "ETF-Rechner, Zinseszins-Simulator und andere hilfreiche Finanz-Tools",
    "features.4.title": "AI-Finanz-Coach",
    "features.4.desc": "beamy beantwortet deine Fragen rund um Finanzen, jederzeit verfügbar.",
    "features.5.title": "Gamification",
    "features.5.desc": "Sammle FinCoins, erreiche Level und bleibe motiviert beim Lernen",
    "features.6.title": "Relevante Lerninhalte",
    "features.6.desc": "Lerne über Finanzen im schweizerischen Kontext",

    "beamy.badge": "Neu: AI-Coach für die Schweiz",
    "beamy.title1": "beamy",
    "beamy.title2": " dein smarter ",
    "beamy.title3": "Finanzcoach",
    "beamy.tagline": "Finanzen googeln war gestern.",
    "beamy.desc": "beamy ist kein Chatbot, sondern dein persönlicher AI-Coach, entwickelt speziell für die Schweizer Realität.",
    "beamy.personal.title": "Persönlich & verständlich",
    "beamy.personal.desc": "Ob ETFs, Steuern oder Risikoprofile: beamy gibt dir verlässliche Antworten, in deinem Tempo.",
    "beamy.personal.choose": "Du wählst, wie ausführlich:",
    "beamy.personal.opt1": "In einem Satz",
    "beamy.personal.opt2": "Mit Beispiel",
    "beamy.personal.opt3": "Als Vergleich",
    "beamy.personal.opt4": "Im Detail",
    "beamy.honest.title": "Ehrlich & transparent",
    "beamy.honest.desc": "Wenn es kompliziert wird, fragt beamy nach statt zu raten.",
    "beamy.swiss.title": "Für die Schweiz gemacht",
    "beamy.swiss.desc": "Säule 3a, Krankenkasse, Steuern, AHV: beamy kennt den Schweizer Kontext.",
    "beamy.cta": "Bereit für smarte Finanzbildung?",
    "beamy.ctaButton": "Frühen Zugang sichern",

    "preview.title": "So sieht finbeam aus",
    "preview.sub": "Entdecke, wie einfach Finanzlernen sein kann",
    "preview.1.title": "Personalisiertes Dashboard",
    "preview.1.desc": "Verfolge deinen Lernfortschritt und erreiche deine Ziele",
    "preview.2.title": "Strukturierte Lernpfade",
    "preview.2.desc": "Von Basics bis zu fortgeschrittenen Themen",
    "preview.3.title": "Interaktive Tools",
    "preview.3.desc": "Rechner und Simulationen für besseres Verständnis",

    "waitlist.success.title": "Du bist drin!",
    "waitlist.success.desc": "Wir haben dich erfolgreich auf die Warteliste gesetzt.",
    "waitlist.success.nextTitle": "So geht's weiter",
    "waitlist.success.next1Title": "Postfach checken",
    "waitlist.success.next1Desc": "Wir haben dir eine Bestätigungs-E-Mail geschickt. Schau auch im Spam-Ordner.",
    "waitlist.success.next2Title": "Early Access folgt",
    "waitlist.success.next2Desc": "Wir melden uns, sobald dein Zugang freigeschaltet ist.",
    "waitlist.success.social": "In der Zwischenzeit folge uns auf Social Media für Updates.",
    "waitlist.title": "Sei eine*r der Ersten!",
    "waitlist.desc": "Melde dich für die Warteliste an und erhalte frühen Zugang zu finbeam.",
    "waitlist.placeholder": "deine.email@beispiel.com",
    "waitlist.submit": "Anmelden",
    "waitlist.submitting": "Wird angemeldet...",
    "waitlist.privacy": "🔒 Deine Daten sind sicher. Wir senden dir nur Updates zu finbeam.",
    "waitlist.error.invalid": "Ungültige E-Mail",
    "waitlist.error.invalidDesc": "Bitte gib eine gültige E-Mail-Adresse ein.",
    "waitlist.error.save": "Anmeldung fehlgeschlagen",
    "waitlist.error.saveDesc": "Wir konnten dich nicht eintragen. Versuche es erneut.",
    "waitlist.error.retry": "Erneut versuchen",
    "waitlist.error.dismiss": "Schliessen",
    "waitlist.successToast": "Erfolgreich angemeldet! 🎉",
    "waitlist.successToastDesc": "Du bist jetzt auf der Warteliste.",

    "footer.desc": "Finanzen. Einfach erklärt. Gebaut in der Schweiz, für alle, denen es nie jemand erklärt hat.",
    "footer.partner": "Partner werden",
    "footer.product": "Produkt",
    "footer.legal": "Rechtliches",
    "footer.datenschutz": "Datenschutz",
    "footer.agb": "AGB",
    "footer.impressum": "Impressum",
    "footer.copyright": "© 2026 finbeam. Alle Rechte vorbehalten.",
    "footer.madeWith": "Made with 💜 for financial education in Switzerland",

    "cookie.text": "Wir verwenden Cookies, um unsere Website zu verbessern.",
    "cookie.learnMore": "Mehr erfahren",
    "cookie.decline": "Ablehnen",
    "cookie.accept": "Akzeptieren",

    "partner.hero.title": "Qualifizierte Leads statt Kaltakquise",
    "partner.hero.sub": "Verbinden Sie sich mit jungen, finanzinteressierten Menschen direkt über unsere Bildungsplattform.",
    "partner.subline": "finbeam vermittelt Finanzwissen auf Augenhöhe und führt Nutzer*innen gezielt zu qualifizierten Berater*innen.",
    "partner.benefits.title": "Was finbeam für Sie als Partner*in bedeutet",
    "partner.b1.title": "Vorgewärmte Leads",
    "partner.b1.desc": "Nutzer*innen kommen aus unserer App, mit Basiswissen und Beratungs-Interesse.",
    "partner.b2.title": "Regionale Steuerung",
    "partner.b2.desc": "Wir achten auf regionale Verfügbarkeit und verhindern Überschneidungen.",
    "partner.b3.title": "Maximale Passung",
    "partner.b3.desc": "Sie erhalten nur Leads, die zu Ihrem Beratungsfokus passen.",
    "partner.b4.title": "Keine Fixkosten",
    "partner.b4.desc": "Sie zahlen nur für geprüfte Kontakte.",
    "partner.form.title": "Wir starten bald mit limitierten Partner*innen",
    "partner.form.sub": "Tragen Sie sich ein, um vorab informiert zu werden.",
    "partner.form.name": "Name / Firma *",
    "partner.form.region": "Region (Kanton oder PLZ) *",
    "partner.form.email": "E-Mail-Adresse *",
    "partner.form.website": "Webseite oder LinkedIn (optional)",
    "partner.form.websitePlaceholder": "z.B. www.beispiel.ch",
    "partner.form.fokus": "Beratungsfokus:",
    "partner.form.fokus.vorsorge": "Vorsorge",
    "partner.form.fokus.versicherung": "Versicherung",
    "partner.form.fokus.anlagen": "Anlagen",
    "partner.form.fokus.steuern": "Steuern",
    "partner.form.fokus.selbstaendigkeit": "Selbständigkeit",
    "partner.form.agree": "Ich bin interessiert, finbeam-Partner zu werden.",
    "partner.form.submit": "Jetzt Partneranfrage stellen",
    "partner.form.submitting": "Wird gesendet...",
    "partner.form.note": "Ihr Eintrag ist unverbindlich.",
    "partner.form.successTitle": "Anfrage erfolgreich gesendet! 🎉",
    "partner.form.successDesc": "Vielen Dank! Wir melden uns bald.",
    "partner.form.errorTitle": "Fehler beim Speichern",
    "partner.form.errorDesc": "Bitte versuchen Sie es erneut.",
    "partner.form.success.title": "Anfrage erhalten",
    "partner.form.success.desc": "Vielen Dank für Ihr Interesse an einer Partnerschaft mit finbeam. Wir melden uns in den nächsten Tagen persönlich bei Ihnen.",
    "partner.form.success.nextTitle": "Wie es weitergeht",
    "partner.form.success.next1Title": "Bestätigungs-E-Mail",
    "partner.form.success.next1Desc": "Sie erhalten in Kürze eine Bestätigung Ihrer Anfrage per E-Mail.",
    "partner.form.success.next2Title": "Persönliches Gespräch",
    "partner.form.success.next2Desc": "Wir vereinbaren einen Call, um die Pilotphase und Details zur Zusammenarbeit zu besprechen.",
    "partner.form.success.note": "Bei Fragen erreichen Sie uns jederzeit unter hello@finbeam.ch",

    "impressum.title": "Impressum",
    "impressum.sub": "Rechtliche Informationen über finbeam",
    "impressum.soon": "Inhalte folgen bald",
    "impressum.desc": "Wir stellen gerade alle rechtlich erforderlichen Informationen zusammen.",
    "impressum.date": "Stand: 2026 • finbeam",

    "agb.title": "Allgemeine Geschäftsbedingungen",
    "agb.sub": "Faire und transparente Nutzungsbedingungen",
    "agb.soon": "Inhalte folgen bald",
    "agb.desc": "Wir erstellen gerade unsere AGB.",
    "agb.date": "Stand: 2026 • finbeam",

    "datenschutz.title": "Datenschutzerklärung",
    "datenschutz.sub": "Deine Privatsphäre ist uns wichtig",
    "datenschutz.secure": "Sichere Daten",
    "datenschutz.secureDesc": "Deine Daten werden verschlüsselt gespeichert",
    "datenschutz.transparent": "Transparenz",
    "datenschutz.transparentDesc": "Volle Kontrolle über deine Informationen",
    "datenschutz.gdpr": "DSGVO-konform",
    "datenschutz.gdprDesc": "Entspricht europäischen Standards",
    "datenschutz.soon": "Inhalte folgen bald",
    "datenschutz.desc": "Wir arbeiten an einer detaillierten Datenschutzerklärung.",
    "datenschutz.date": "Stand: 2026 • finbeam",

    "problem.title": "Niemand hat's dir je richtig erklärt.",
    "problem.sub": "Kennst du diese Gedanken? Damit bist du nicht allein.",
    "problem.1.title": "«Ich weiss gar nicht, wo ich anfangen soll.»",
    "problem.1.desc": "Tausend Meinungen, tausend Tipps – und keine davon fühlt sich nach deinem Leben an.",
    "problem.2.title": "«Wem soll ich das überhaupt glauben?»",
    "problem.2.desc": "Jeder will dir etwas verkaufen. Neutral erklärt bekommt es dir fast niemand.",
    "problem.3.title": "«Säule 3a? AHV? Keine Ahnung.»",
    "problem.3.desc": "Die meisten Apps reden über 401(k) und IRS. Nur eben nicht über die Schweiz.",

    "topics.title": "Von Budget bis Vorsorge",
    "topics.sub": "14 Themenwelten, die dein Finanzwissen Schritt für Schritt aufbauen",
    "topics.budget": "Budgetplanung",
    "topics.taxes": "Steuern",
    "topics.pillar3a": "Säule 3a",
    "topics.etfs": "ETFs",
    "topics.saving": "Sparen",
    "topics.insurance": "Versicherungen",
    "topics.realestate": "Immobilien",
    "topics.crypto": "Kryptowährungen",
    "topics.investing": "Anlegen & Investieren",
    "topics.basics": "Finanz-Grundlagen",
    "topics.debt": "Schulden & Kredite",
    "topics.credit": "Kreditkarten",
    "topics.sustainability": "Nachhaltiges Investieren",
    "topics.pension": "Vorsorge & AHV",

    "faq.title": "Häufig gestellte Fragen",
    "faq.subtitle": "Alles was du über finbeam wissen musst",
    "faq.q1": "Was ist finbeam?",
    "faq.a1": "finbeam ist eine Schweizer App für Finanzbildung: unabhängig, neutral und auf den Schweizer Kontext zugeschnitten.",
    "faq.q2": "Für wen ist finbeam gedacht?",
    "faq.a2": "Für alle, die ihre Finanzen besser verstehen möchten, egal ob Berufseinsteiger*in oder erfahrene Sparer*in.",
    "faq.q3": "Was kostet finbeam?",
    "faq.a3": "Der Einstieg ist kostenlos. Setze dich auf die Warteliste für früheren Zugang.",
    "faq.q4": "Was ist beamy?",
    "faq.a4": "beamy ist dein persönlicher AI-Coach in der App: verständlich und sympathisch.",
    "faq.q5": "Wann ist finbeam verfügbar?",
    "faq.a5": "finbeam ist in Entwicklung. Trag dich auf die Warteliste ein, um früh informiert zu werden.",

    "eyebrow.appPreview": "App Preview",
    "eyebrow.faq": "FAQ",
    "eyebrow.features": "Features",
    "eyebrow.problem": "Das Problem",
    "eyebrow.topics": "Themenwelten",
    "eyebrow.earlyAccess": "Early Access",
    "eyebrow.legal": "Rechtliches",
    "eyebrow.benefits": "Vorteile",
    "eyebrow.pilot": "Pilotphase",
    "eyebrow.partnerHero": "Für Partner*innen",
    "hero.statusBadge": "finbeam · Bald verfügbar",
    "hero.beamyNote": "Hoi, ich bin beamy 👋",

    "beamy.note.topics": "Kein Fachchinesisch. Versprochen.",
    "beamy.bubble": "«Frag mich alles. Auch das, was du dich sonst nicht zu fragen traust.»",

    "eyebrow.about": "Hinter finbeam",
    "about.title1": "Wir bauen das, was uns ",
    "about.title2": "selbst gefehlt hat",
    "about.p1": "Erste Lohnabrechnung, erste Steuererklärung, erste Frage zur Säule 3a – und niemand, der es dir ohne Verkaufsabsicht erklärt.",
    "about.p2": "finbeam entsteht in der Schweiz, für Schweizer Realität: Krankenkasse, AHV, 3a, Steuern. Kein Produktverkauf, keine Provisionen. Nur Finanzbildung, die du wirklich verstehst.",
    "about.point1": "Unabhängig",
    "about.point1desc": "Wir verkaufen keine Finanzprodukte.",
    "about.point2": "Schweizer Kontext",
    "about.point2desc": "3a, AHV, Krankenkasse – korrekt erklärt.",
    "about.point3": "Für Einsteiger*innen",
    "about.point3desc": "Vorwissen? Brauchst du keins.",

    "notFound.eyebrow": "Fehler 404",
    "notFound.title1": "Seite",
    "notFound.title2": "verloren",
    "notFound.desc": "Diese Seite existiert nicht oder wurde verschoben.",
    "notFound.cta": "Zur Startseite",

    "mock.home.hi": "Hi",
    "mock.home.chapter": "Kapitel 3 · Läuft",
    "mock.home.headline1": "Budget bauen",
    "mock.home.headline2": "wie ein",
    "mock.home.headline3": "Boss",
    "mock.home.continueAt": "Weiter bei",
    "mock.home.scenario": "Szenario: 3'200.– am 25.",
    "mock.home.scenarioMeta": "5 Min · +120 XP",
    "mock.home.startLesson": "Lektion starten",
    "mock.home.stat1Label": "Level",
    "mock.home.stat1Sub": "2840 / 3200 XP",
    "mock.home.stat2Label": "Rang",
    "mock.home.stat2Sub": "in der Schweiz",
    "mock.home.stat3Label": "Dabei",
    "mock.home.stat3Value": "47T",
    "mock.home.stat3Sub": "seit Start",
    "mock.home.todayTitle": "Heute für dich",
    "mock.home.todayCount": "4 Dinge",
    "mock.home.streakTitle": "12 Tage Streak",
    "mock.home.streakSub": "Noch 3 Tage bis 'Zwei Wochen Flow'",

    "mock.path.topbar": "Dein Pfad · 6 Kapitel",
    "mock.path.headline1": "Deine",
    "mock.path.headline2": "Missionen",
    "mock.path.done": "✓ DONE",
    "mock.path.active": "● LÄUFT",
    "mock.path.lessonsUnit": "Lektionen",
    "mock.path.progressSuffix": "% durch",
    "mock.path.c1.title": "Erstes Geld, erste Fragen",
    "mock.path.c1.sub": "Vom Lehrlingslohn zum Überblick",
    "mock.path.c2.title": "Miete, Krankenkasse, Serafe",
    "mock.path.c2.sub": "Die Fixkosten-Realität der Schweiz",
    "mock.path.c3.title": "Budget bauen wie ein Boss",
    "mock.path.c3.sub": "50/30/20, auf Schweizerisch",
    "mock.path.c4.title": "Sparen ohne Schmerz",
    "mock.path.c4.sub": "Wie aus 5.– pro Tag etwas wird",

    "mock.lesson.chapter": "Kapitel 3 · Lektion 5/8",
    "mock.lesson.scenarioBadge": "SZENARIO",
    "mock.lesson.eyebrow": "Was machst du?",
    "mock.lesson.headlinePart1": "3'200.– und der",
    "mock.lesson.headlinePart2": "25.",
    "mock.lesson.body": "Dein Lohn ist gekommen. Krankenkasse & Miete sind offen. Am Wochenende ist Festival.",
    "mock.lesson.statKonto": "Konto",
    "mock.lesson.statOffen": "Offen",
    "mock.lesson.statFrei": "Frei",
    "mock.lesson.choose": "Wähl eine Option:",
    "mock.lesson.optA.title": "Fixkosten sofort, Rest aufteilen",
    "mock.lesson.optA.sub": "Die Erwachsenen-Variante",
    "mock.lesson.optB.title": "Festival first, Miete zahlt sich selbst",
    "mock.lesson.optB.sub": "Bist du sicher?",
    "mock.home.chapterTitle": "Kapitel 3 wartet",
    "mock.home.chapterSub": "5 von 8 Lektionen – weiter bei 'Szenario: 3'200.–'",

    "mock.nav.path": "Pfad",
    "mock.nav.profile": "Profil",

    "mock.chat.subtitle": "Dein smarter Finanzcoach",
    "mock.chat.user1": "Ich blick bei der Aufgabe nicht durch — kannst du mir die Lösung sagen?",
    "mock.chat.bot1": "Die Lösung verrate ich dir nicht — das nimmt dir den Aha-Moment 😉 Aber ich helf dir hin. Welche Kosten musst du auf jeden Fall zahlen?",
    "mock.chat.user2": "Krankenkasse und Miete, oder?",
    "mock.chat.bot2": "Genau — das sind deine Fixkosten. Faustregel: Fixkosten zuerst, dann das Variable.",
    "mock.chat.bot3": "Jetzt überleg: Was passiert mit deinem Puffer, wenn du erst Festival machst und dann zahlst?",
    "mock.chat.chip1": "Mehr Hinweis bitte",
    "mock.chat.chip2": "Ich glaub, ich hab's",
    "mock.chat.action1": "📋 Zusammenfassen",
    "mock.chat.action2": "🧮 Beispiel rechnen",
    "mock.chat.action3": "🔴 Quiz starten",
  },
  en: {
    "nav.features": "Features",
    "nav.appPreview": "App Preview",
    "nav.waitlist": "Waitlist",
    "nav.menuOpen": "Open menu",

    "hero.main": "Financial education you ",
    "hero.highlight": "actually need",
    "hero.sub": "In your language, in your everyday life. Learn finance playfully and practically.",
    "hero.cta": "Get early access",
    "hero.free": "Free",
    "hero.ai": "AI Coach",
    "hero.swiss": "Swiss know-how",

    "features.title": "Why finbeam?",
    "features.sub": "Everything school never taught you – in small, doable bites.",
    "features.1.title": "Personalized Learning Goals",
    "features.1.desc": "Set realistic goals and track your progress in real time",
    "features.2.title": "Interactive Modules",
    "features.2.desc": "Learn through quizzes, challenges and hands-on exercises",
    "features.3.title": "Practical Tools",
    "features.3.desc": "ETF calculator, compound interest simulator and more",
    "features.4.title": "AI Finance Coach",
    "features.4.desc": "beamy answers your finance questions, anytime",
    "features.5.title": "Gamification",
    "features.5.desc": "Collect FinCoins, reach levels and stay motivated",
    "features.6.title": "Relevant Content",
    "features.6.desc": "Learn about finance in the Swiss context",

    "beamy.badge": "New: AI Coach for Switzerland",
    "beamy.title1": "beamy",
    "beamy.title2": " your smart ",
    "beamy.title3": "Finance Coach",
    "beamy.tagline": "Googling finance is so yesterday.",
    "beamy.desc": "beamy isn't a chatbot, it's your personal AI coach built for Swiss reality.",
    "beamy.personal.title": "Personal & understandable",
    "beamy.personal.desc": "Whether ETFs, taxes or risk profiles: beamy gives reliable answers at your pace.",
    "beamy.personal.choose": "Choose how detailed:",
    "beamy.personal.opt1": "In one sentence",
    "beamy.personal.opt2": "With an example",
    "beamy.personal.opt3": "As a comparison",
    "beamy.personal.opt4": "In detail",
    "beamy.honest.title": "Honest & transparent",
    "beamy.honest.desc": "When things get complicated, beamy asks instead of guessing.",
    "beamy.swiss.title": "Made for Switzerland",
    "beamy.swiss.desc": "Pillar 3a, health insurance, taxes, AHV: beamy knows the Swiss context.",
    "beamy.cta": "Ready for smart financial education?",
    "beamy.ctaButton": "Get early access",

    "preview.title": "This is what finbeam looks like",
    "preview.sub": "Discover how easy learning finance can be",
    "preview.1.title": "Personalized Dashboard",
    "preview.1.desc": "Track your learning progress and reach your goals",
    "preview.2.title": "Structured Learning Paths",
    "preview.2.desc": "From basics to advanced topics",
    "preview.3.title": "Interactive Tools",
    "preview.3.desc": "Calculators and simulations for better understanding",

    "waitlist.success.title": "You're in!",
    "waitlist.success.desc": "We've successfully added you to the waitlist.",
    "waitlist.success.nextTitle": "What happens next",
    "waitlist.success.next1Title": "Check your inbox",
    "waitlist.success.next1Desc": "We just sent you a confirmation email.",
    "waitlist.success.next2Title": "Early Access incoming",
    "waitlist.success.next2Desc": "We'll reach out as soon as your access is unlocked.",
    "waitlist.success.social": "In the meantime, follow us on social media.",
    "waitlist.title": "Be one of the first!",
    "waitlist.desc": "Sign up for our waitlist and get early access to finbeam.",
    "waitlist.placeholder": "your.email@example.com",
    "waitlist.submit": "Sign up",
    "waitlist.submitting": "Signing up...",
    "waitlist.privacy": "🔒 Your data is safe.",
    "waitlist.error.invalid": "Invalid email",
    "waitlist.error.invalidDesc": "Please enter a valid email.",
    "waitlist.error.save": "Sign-up failed",
    "waitlist.error.saveDesc": "We couldn't add you. Please try again.",
    "waitlist.error.retry": "Try again",
    "waitlist.error.dismiss": "Dismiss",
    "waitlist.successToast": "Successfully signed up! 🎉",
    "waitlist.successToastDesc": "You're now on the waitlist.",

    "footer.desc": "Finance. Made simple. Built in Switzerland, for everyone nobody ever explained it to.",
    "footer.partner": "Become a partner",
    "footer.product": "Product",
    "footer.legal": "Legal",
    "footer.datenschutz": "Privacy Policy",
    "footer.agb": "Terms",
    "footer.impressum": "Imprint",
    "footer.copyright": "© 2026 finbeam. All rights reserved.",
    "footer.madeWith": "Made with 💜 for financial education in Switzerland",

    "cookie.text": "We use cookies to improve our website.",
    "cookie.learnMore": "Learn more",
    "cookie.decline": "Decline",
    "cookie.accept": "Accept",

    "partner.hero.title": "Qualified leads instead of cold calling",
    "partner.hero.sub": "Connect with young, financially interested people through our platform.",
    "partner.subline": "finbeam teaches financial literacy at eye level and guides users to qualified advisors.",
    "partner.benefits.title": "What finbeam means for you as a partner",
    "partner.b1.title": "Pre-qualified leads",
    "partner.b1.desc": "Users come from our app with basic knowledge.",
    "partner.b2.title": "Regional targeting",
    "partner.b2.desc": "We ensure regional availability and prevent overlaps.",
    "partner.b3.title": "Maximum fit",
    "partner.b3.desc": "You only receive leads that match your focus.",
    "partner.b4.title": "No fixed costs",
    "partner.b4.desc": "You only pay for verified contacts.",
    "partner.form.title": "We're starting soon with limited partners",
    "partner.form.sub": "Sign up to be informed in advance.",
    "partner.form.name": "Name / Company *",
    "partner.form.region": "Region (Canton or ZIP) *",
    "partner.form.email": "Email *",
    "partner.form.website": "Website or LinkedIn (optional)",
    "partner.form.websitePlaceholder": "e.g. www.example.ch",
    "partner.form.fokus": "Advisory focus:",
    "partner.form.fokus.vorsorge": "Retirement",
    "partner.form.fokus.versicherung": "Insurance",
    "partner.form.fokus.anlagen": "Investments",
    "partner.form.fokus.steuern": "Taxes",
    "partner.form.fokus.selbstaendigkeit": "Self-employment",
    "partner.form.agree": "I'm interested in becoming a finbeam partner.",
    "partner.form.submit": "Submit partner request",
    "partner.form.submitting": "Sending...",
    "partner.form.note": "Your entry is non-binding.",
    "partner.form.successTitle": "Request sent! 🎉",
    "partner.form.successDesc": "Thanks! We'll get back to you soon.",
    "partner.form.errorTitle": "Error saving",
    "partner.form.errorDesc": "Please try again.",
    "partner.form.success.title": "Request received",
    "partner.form.success.desc": "Thank you for your interest in partnering with finbeam. We will get back to you personally in the next few days.",
    "partner.form.success.nextTitle": "What's next",
    "partner.form.success.next1Title": "Confirmation email",
    "partner.form.success.next1Desc": "You will receive a confirmation of your request shortly.",
    "partner.form.success.next2Title": "Personal call",
    "partner.form.success.next2Desc": "We will schedule a call to discuss the pilot phase and partnership details.",
    "partner.form.success.note": "If you have any questions, reach us anytime at hello@finbeam.ch",

    "impressum.title": "Imprint",
    "impressum.sub": "Legal information about finbeam",
    "impressum.soon": "Content coming soon",
    "impressum.desc": "We're compiling all required information.",
    "impressum.date": "As of: 2026 • finbeam",

    "agb.title": "Terms and Conditions",
    "agb.sub": "Fair and transparent terms",
    "agb.soon": "Content coming soon",
    "agb.desc": "We're creating our terms.",
    "agb.date": "As of: 2026 • finbeam",

    "datenschutz.title": "Privacy Policy",
    "datenschutz.sub": "Your privacy matters to us",
    "datenschutz.secure": "Secure Data",
    "datenschutz.secureDesc": "Your data is encrypted",
    "datenschutz.transparent": "Transparency",
    "datenschutz.transparentDesc": "Full control over your information",
    "datenschutz.gdpr": "GDPR compliant",
    "datenschutz.gdprDesc": "Meets European standards",
    "datenschutz.soon": "Content coming soon",
    "datenschutz.desc": "We're working on a detailed privacy policy.",
    "datenschutz.date": "As of: 2026 • finbeam",

    "problem.title": "Nobody ever really explained it to you.",
    "problem.sub": "Sound familiar? You're far from alone.",
    "problem.1.title": "\u201cI don't even know where to start.\u201d",
    "problem.1.desc": "A thousand opinions, a thousand tips \u2013 and none of them sound like your life.",
    "problem.2.title": "\u201cWho am I even supposed to believe?\u201d",
    "problem.2.desc": "Everyone wants to sell you something. Almost nobody just explains it.",
    "problem.3.title": "\u201cPillar 3a? AHV? No clue.\u201d",
    "problem.3.desc": "Most apps talk 401(k) and IRS. Just not about Switzerland.",

    "topics.title": "From budgeting to retirement",
    "topics.sub": "14 topic areas that build your financial knowledge",
    "topics.budget": "Budgeting",
    "topics.taxes": "Taxes",
    "topics.pillar3a": "Pillar 3a",
    "topics.etfs": "ETFs",
    "topics.saving": "Saving",
    "topics.insurance": "Insurance",
    "topics.realestate": "Real Estate",
    "topics.crypto": "Cryptocurrency",
    "topics.investing": "Investing",
    "topics.basics": "Finance Basics",
    "topics.debt": "Debt & Loans",
    "topics.credit": "Credit Cards",
    "topics.sustainability": "Sustainable Investing",
    "topics.pension": "Pension & AHV",

    "faq.title": "Frequently Asked Questions",
    "faq.subtitle": "Everything you need to know about finbeam",
    "faq.q1": "What is finbeam?",
    "faq.a1": "finbeam is a Swiss app for financial education: independent and tailored to Switzerland.",
    "faq.q2": "Who is finbeam for?",
    "faq.a2": "For anyone who wants to better understand their finances.",
    "faq.q3": "How much does finbeam cost?",
    "faq.a3": "Getting started is free. Join the waitlist now.",
    "faq.q4": "What is beamy?",
    "faq.a4": "beamy is your personal AI coach in the app: clear and approachable.",
    "faq.q5": "When will finbeam be available?",
    "faq.a5": "finbeam is currently in development. Join the waitlist to be the first to know.",

    "eyebrow.appPreview": "App Preview",
    "eyebrow.faq": "FAQ",
    "eyebrow.features": "Features",
    "eyebrow.problem": "The Problem",
    "eyebrow.topics": "Topics",
    "eyebrow.earlyAccess": "Early Access",
    "eyebrow.legal": "Legal",
    "eyebrow.benefits": "Benefits",
    "eyebrow.pilot": "Pilot Phase",
    "eyebrow.partnerHero": "For Partners",
    "hero.statusBadge": "finbeam · Coming soon",
    "hero.beamyNote": "Hi, I'm beamy 👋",

    "beamy.note.topics": "No jargon. Promised.",
    "beamy.bubble": "\u201cAsk me anything. Especially the stuff you'd never ask out loud.\u201d",

    "eyebrow.about": "Behind finbeam",
    "about.title1": "We're building what we ",
    "about.title2": "were missing",
    "about.p1": "First payslip, first tax return, first question about Pillar 3a \u2013 and nobody to explain it without a sales pitch.",
    "about.p2": "finbeam is built in Switzerland, for Swiss reality: health insurance, AHV, 3a, taxes. No products to sell, no commissions. Just financial education you actually understand.",
    "about.point1": "Independent",
    "about.point1desc": "We don't sell financial products.",
    "about.point2": "Swiss context",
    "about.point2desc": "3a, AHV, health insurance \u2013 done right.",
    "about.point3": "For beginners",
    "about.point3desc": "No prior knowledge needed.",

    "notFound.eyebrow": "Error 404",
    "notFound.title1": "Page",
    "notFound.title2": "not found",
    "notFound.desc": "This page doesn't exist or has been moved.",
    "notFound.cta": "Back to home",

    "mock.home.hi": "Hi",
    "mock.home.chapter": "Chapter 3 · In progress",
    "mock.home.headline1": "Build a budget",
    "mock.home.headline2": "like a",
    "mock.home.headline3": "boss",
    "mock.home.continueAt": "Pick up at",
    "mock.home.scenario": "Scenario: CHF 3,200 on the 25th",
    "mock.home.scenarioMeta": "5 min · +120 XP",
    "mock.home.startLesson": "Start lesson",
    "mock.home.stat1Label": "Level",
    "mock.home.stat1Sub": "2,840 / 3,200 XP",
    "mock.home.stat2Label": "Rank",
    "mock.home.stat2Sub": "in Switzerland",
    "mock.home.stat3Label": "Streak",
    "mock.home.stat3Value": "47d",
    "mock.home.stat3Sub": "since day one",
    "mock.home.todayTitle": "For you today",
    "mock.home.todayCount": "4 things",
    "mock.home.streakTitle": "12-day streak",
    "mock.home.streakSub": "3 more days to 'Two-week flow'",

    "mock.path.topbar": "Your path · 6 chapters",
    "mock.path.headline1": "Your",
    "mock.path.headline2": "missions",
    "mock.path.done": "✓ DONE",
    "mock.path.active": "● IN PROGRESS",
    "mock.path.lessonsUnit": "lessons",
    "mock.path.progressSuffix": "% done",
    "mock.path.c1.title": "First paycheque, first questions",
    "mock.path.c1.sub": "From apprentice wage to overview",
    "mock.path.c2.title": "Rent, health insurance, Serafe",
    "mock.path.c2.sub": "The Swiss fixed-cost reality",
    "mock.path.c3.title": "Build a budget like a boss",
    "mock.path.c3.sub": "50/30/20, the Swiss way",
    "mock.path.c4.title": "Saving without the sting",
    "mock.path.c4.sub": "How CHF 5 a day adds up",

    "mock.lesson.chapter": "Chapter 3 · Lesson 5/8",
    "mock.lesson.scenarioBadge": "SCENARIO",
    "mock.lesson.eyebrow": "What do you do?",
    "mock.lesson.headlinePart1": "CHF 3,200 and the",
    "mock.lesson.headlinePart2": "25th",
    "mock.lesson.body": "Your salary just landed. Health insurance and rent are still open.",
    "mock.lesson.statKonto": "Account",
    "mock.lesson.statOffen": "Open",
    "mock.lesson.statFrei": "Free",
    "mock.lesson.choose": "Pick an option:",
    "mock.lesson.optA.title": "Fixed costs first, split the rest",
    "mock.lesson.optA.sub": "The grown-up move",
    "mock.lesson.optB.title": "Festival first, rent figures itself out",
    "mock.lesson.optB.sub": "You sure?",
    "mock.lesson.optC.title": "Lend it — with a written note",
    "mock.lesson.optC.sub": "Friendship plus structure",

    "mock.home.chapterTitle": "Chapter 3 is waiting",
    "mock.home.chapterSub": "5 of 8 lessons – pick up at 'Scenario: CHF 3,200'",

    "mock.nav.path": "Path",
    "mock.nav.profile": "Profile",

    "mock.chat.subtitle": "Your smart finance coach",
    "mock.chat.user1": "I'm stuck on this task — can you tell me the answer?",
    "mock.chat.bot1": "I won't spoil it — that would take away your aha moment 😉 But I'll guide you. Which costs do you have to pay no matter what?",
    "mock.chat.user2": "Health insurance and rent, right?",
    "mock.chat.bot2": "Exactly — those are your fixed costs. Rule of thumb: fixed costs first, then variable.",
    "mock.chat.bot3": "Now think: what happens to your buffer if you go to the festival first and pay later?",
    "mock.chat.chip1": "More hints please",
    "mock.chat.chip2": "I think I got it",
    "mock.chat.action1": "📋 Summarise",
    "mock.chat.action2": "🧮 Calculate example",
    "mock.chat.action3": "🔴 Start quiz",
  },
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem("finbeam_lang");
    if (saved === "en" || saved === "de") return saved;

    // Detect browser/system language; default to German for everything non-English
    if (typeof navigator !== "undefined") {
      const langs = [
        ...(navigator.languages || []),
        navigator.language,
      ].filter(Boolean);
      for (const l of langs) {
        const code = l.toLowerCase().split("-")[0];
        if (code === "de") return "de";
        if (code === "en") return "en";
      }
    }
    return "de";
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem("finbeam_lang", lang);
    document.documentElement.lang = lang;
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = (key: string): string => translations[language][key] || key;

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
